import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Check, X} from "lucide-react";
import {useSearchParams} from "react-router-dom";
import {PricePreviewItem} from '@paddle/paddle-js';
import {PRICING_PLANS} from "../../../pricing";
import {LineItem} from "@paddle/paddle-js/types/price-preview/price-preview";
import {useAuth} from "../contexts/AuthContext";
import {getLoginRedirectUrl} from "../pages/Login";
import {usePaddle} from "../contexts/PaddleContext";

export default function SubscriptionSection() {

    const [params, setParams] = useSearchParams()
    const auth = useAuth()

    const paddle = usePaddle()
    const [prices, setPrices] = useState<Record<string, LineItem>>({})
    const [currencyCode, setCurrencyCode] = useState<string>("USD")

    useEffect(() => {
        if (!paddle){
            return
        }

        const priceIds = PRICING_PLANS
            .flatMap(x => [x.monthly, x.yearly])
            .filter(x => !!x)

        paddle.PricePreview({
            items: priceIds.map<PricePreviewItem>(x => ({ priceId: x!, quantity: 1 }))
        }).then(pricePreview => {
            const prices: Record<string, LineItem> = {}
            for (let item of pricePreview.data.details.lineItems) {
                prices[item.price.id] = item
            }
            setPrices(prices)
            setCurrencyCode(pricePreview.data.currencyCode)
        });

    }, [paddle]);

    const openCheckout = useCallback((priceId: string)=> {
        if (!paddle){
            console.error("Failed to open checkout - Paddle not initialized")
            return
        }

        if (!auth.isLoggedIn || !auth.email){
            window.location.assign(getLoginRedirectUrl(`/?subscribe=${priceId}`))
            return
        }

        paddle.Checkout.open({
            items: [{ priceId: priceId, quantity: 1 }],
            customer: {
                email: auth.email,
            }
        });
    }, [paddle, auth])

    // Auto-open checkout once when redirected back with ?subscribe=<priceId> (e.g. after login).
    const autoCheckoutDone = useRef(false)
    useEffect(() => {
        if (!paddle || autoCheckoutDone.current){
            return
        }

        const subscribe = params.get("subscribe")
        // Wait until the requested price has loaded before opening checkout.
        if (!subscribe || !prices[subscribe]){
            return
        }

        autoCheckoutDone.current = true

        // Strip the param from the URL (replace, no history entry) so a re-render or the back
        // button can't reopen checkout.
        setParams(prev => {
            const next = new URLSearchParams(prev)
            next.delete("subscribe")
            return next
        }, {replace: true})

        openCheckout(subscribe)
    }, [paddle, prices, openCheckout, params, setParams])

    const [monthly, setMonthly] = useState(false)

    const formatMoney = useCallback((amount: number, opts?: { trimZeros?: boolean }) => {
        const isInt = Number.isInteger(amount)
        const fractionDigits = opts?.trimZeros && isInt ? 0 : 2
        try {
            return new Intl.NumberFormat(undefined, {
                style: "currency",
                currency: currencyCode,
                minimumFractionDigits: fractionDigits,
                maximumFractionDigits: 2,
            }).format(amount)
        } catch {
            return `${currencyCode} ${amount.toFixed(fractionDigits)}`
        }
    }, [currencyCode])

    // Pre-compute the max numeric value for each feature position across all plans,
    // so progress bars are normalized against the best-in-class plan.
    const featureMaxes = useMemo(() => {
        const maxLen = Math.max(...PRICING_PLANS.map(p => p.features.length))
        const out: number[] = []
        for (let i = 0; i < maxLen; i++) {
            let m = 0
            for (const plan of PRICING_PLANS) {
                const feat = plan.features[i]
                if (!feat) continue
                const parsed = parseFeature(feat)
                if (parsed.value != null && parsed.value > m) m = parsed.value
            }
            out.push(m)
        }
        return out
    }, [])

    const zeroFormatted = (() => {
        try {
            return new Intl.NumberFormat(undefined, {
                style: "currency",
                currency: currencyCode,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(0)
        } catch {
            return "$0"
        }
    })()

    const onSignup = () => {
        window.location.assign(getLoginRedirectUrl("/pricing"))
    }

    // TODO remove for prod
    if (!params.has("test")){
        return null
    }

    return (
        <div className="w-full">
            {/* Free-tier banner (only for signed-out visitors) */}
            {!auth.isLoggedIn && (
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 md:gap-10 items-center mb-14">
                    <div>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">
                            Free, until you grow
                        </h2>
                        <p className="text-neutral-400 max-w-md">
                            Spin up your first Rust server and explore every dashboard feature with our free tier.
                            Upgrade only when your community outgrows it.
                        </p>
                    </div>
                    <div className="rounded-2xl border border-neutral-800 bg-neutral-900 p-5 md:min-w-[18rem]">
                        <div className="flex items-baseline justify-end mb-3">
                            <span className="text-4xl font-extrabold text-white">{zeroFormatted}</span>
                            <span className="text-neutral-400 ml-2">forever</span>
                        </div>
                        <button onClick={onSignup}
                                className="w-full rounded-xl py-3 font-semibold text-white bg-gradient-to-r from-primary to-sky-400 hover:from-primary-hover hover:to-sky-300 transition-all shadow-lg shadow-primary/20">
                            Sign up
                        </button>
                        <p className="text-center text-xs text-neutral-400 mt-2">No credit card needed</p>
                    </div>
                </div>
            )}

            {/* Billing toggle */}
            <div className="flex justify-center mb-10">
                <div className="flex items-center gap-6 text-sm">
                    <BillingOption
                        label="Monthly"
                        selected={monthly}
                        onClick={() => setMonthly(true)}
                    />
                    <BillingOption
                        label="Annually"
                        selected={!monthly}
                        onClick={() => setMonthly(false)}
                        badge="Save up to 17%"
                    />
                </div>
            </div>

            {/* Gradient-bordered card group */}
            <div className="rounded-2xl bg-gradient-to-br from-primary via-sky-400 to-primary p-[1.5px] shadow-xl shadow-primary/10">
                <div className="rounded-2xl bg-neutral-900 grid grid-cols-1 md:grid-cols-3 overflow-hidden">
                    {PRICING_PLANS.map((plan, index) => {
                        const priceId = (monthly ? plan.monthly : plan.yearly)!
                        const priceMo = prices[plan.monthly!]
                        const priceYr = prices[plan.yearly!]

                        if (!priceYr || !priceMo) {
                            return (
                                <div key={index} className="p-8 flex flex-col">
                                    <div className="h-8 w-24 bg-neutral-800 rounded animate-pulse mb-4"/>
                                    <div className="h-12 w-32 bg-neutral-800 rounded animate-pulse mb-8"/>
                                    <div className="h-10 w-full bg-neutral-800 rounded animate-pulse"/>
                                </div>
                            )
                        }

                        // Paddle returns totals in the smallest currency unit (e.g. cents).
                        // Keep arithmetic in integer cents to avoid floating-point drift
                        // (e.g. 14.95*12 - 12.95*12 == 23.999999999999996), then convert once at the end.
                        const yrTotalCents = parseFloat(priceYr.unitTotals.total)
                        const moTotalCents = parseFloat(priceMo.unitTotals.total)
                        const displayPrice = (monthly ? moTotalCents : yrTotalCents / 12) / 100
                        const savings = ((moTotalCents * 12) - yrTotalCents) / 100

                        return (
                            <PlanColumn
                                key={index}
                                planName={plan.name}
                                featured={plan.featured}
                                displayPrice={formatMoney(displayPrice)}
                                billingNote={monthly ? "billed monthly" : "billed annually"}
                                savingsNote={!monthly && savings > 0 ? `Save ${formatMoney(savings, {trimZeros: true})} a year` : undefined}
                                features={plan.features}
                                featureMaxes={featureMaxes}
                                onSubscribe={() => openCheckout(priceId)}
                                isLast={index === PRICING_PLANS.length - 1}
                            />
                        )
                    })}
                </div>
            </div>
        </div>
    );
}

function BillingOption(props: {
    label: string,
    selected: boolean,
    onClick: () => void,
    badge?: string,
}) {
    return (
        <button
            onClick={props.onClick}
            className="flex items-center gap-2 group focus:outline-none"
        >
            <span className={`size-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                props.selected ? "border-primary" : "border-neutral-500 group-hover:border-neutral-300"
            }`}>
                {props.selected && <span className="size-2 rounded-full bg-primary"/>}
            </span>
            <span className={`font-medium ${props.selected ? "text-white" : "text-neutral-400 group-hover:text-neutral-200"}`}>
                {props.label}
            </span>
            {props.badge && (
                <span className="ml-1 inline-block bg-green-600 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                    {props.badge}
                </span>
            )}
        </button>
    )
}

// Parse a feature string for display.
//  - "X foo"      → crossed out (excluded feature, no quantity)
//  - "0 foo"      → crossed out (zero-quantity feature) + bar at 0%
//  - "20 foo"     → included + progress bar proportional to the cross-plan max
//  - "1,000 foo"  → same, thousands separator tolerated
//  - "anything"   → included, no bar
function parseFeature(feat: string): { value: number | null; crossed: boolean; displayText: string } {
    if (/^X\s/.test(feat)) {
        return {value: null, crossed: true, displayText: feat.slice(2)}
    }
    const match = feat.match(/^([\d,]+(?:\.\d+)?)\s/)
    if (match) {
        const num = parseFloat(match[1].replace(/,/g, ""))
        if (!isNaN(num)) {
            return {value: num, crossed: num === 0, displayText: feat}
        }
    }
    return {value: null, crossed: false, displayText: feat}
}

function PlanColumn(props: {
    planName: string,
    featured: boolean,
    displayPrice: string,
    billingNote: string,
    savingsNote?: string,
    features: string[],
    featureMaxes: number[],
    onSubscribe: () => void,
    isLast: boolean,
}) {
    return (
        <div className={`relative p-7 flex flex-col ${!props.isLast ? "md:border-r border-neutral-800" : ""} ${
            props.featured ? "bg-gradient-to-b from-primary/10 to-transparent" : ""
        }`}>
            {props.featured && (
                <div className="absolute top-4 right-4 bg-primary text-white text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full">
                    Most popular
                </div>
            )}

            <div className="text-lg font-semibold text-white mb-5">
                {props.planName}
            </div>

            <div className="mb-1">
                <span className="text-4xl font-extrabold text-white">{props.displayPrice}</span>
                <span className="text-neutral-400 ml-1">/mo</span>
            </div>
            <div className="text-sm text-neutral-400">{props.billingNote}</div>
            <div className="text-sm text-green-400 h-5 mt-1">
                {props.savingsNote ?? " "}
            </div>

            <button
                onClick={props.onSubscribe}
                className="mt-6 w-full bg-white hover:bg-neutral-200 text-neutral-900 font-semibold py-3 rounded-lg transition-colors"
            >
                Get {props.planName}
            </button>

            <ul className="mt-7 space-y-4">
                {props.features.map((feat, i) => {
                    const parsed = parseFeature(feat)
                    const max = props.featureMaxes[i] ?? 0
                    const showBar = parsed.value != null && max > 0
                    const pct = showBar ? Math.min(1, parsed.value! / max) : 0

                    return (
                        <li key={i} className="text-sm">
                            <div className="flex items-center gap-2">
                                {parsed.crossed
                                    ? <X className="size-4 text-neutral-500 shrink-0"/>
                                    : <Check className="size-4 text-primary shrink-0"/>
                                }
                                <span className={parsed.crossed ? "text-neutral-500" : "text-neutral-200"}>
                                    {parsed.displayText}
                                </span>
                            </div>
                            {showBar && (
                                <div className="ml-6 mt-1.5 h-0.5 rounded-full bg-neutral-800 overflow-hidden">
                                    <div
                                        className={parsed.crossed ? "h-full bg-neutral-700" : "h-full bg-primary"}
                                        style={{width: `${pct * 100}%`}}
                                    />
                                </div>
                            )}
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}
